/**
 * One playlist in the playlists list.
 *
 * Built as the song row is (songs/SongCard): a role="button" container with real
 * buttons inside it, each stopping propagation. Same reason — nesting a <button>
 * inside a <button> is invalid HTML that browsers resolve unpredictably — and the same
 * consequence: pressing Play must not also open the playlist.
 *
 * Play is visible and accent-coloured because it is the point of a playlist; rename
 * and delete are in the More menu, matching the library row's division of primary and
 * secondary actions.
 *
 * THE PLAY BUTTON IS A PLAY/PAUSE BUTTON NOW, which is the correction this pass makes. It
 * used to be a permanent Play triangle wired to a permanent "start this playlist": press it
 * on the playlist you were already listening to and playback jumped back to track 1, and a
 * screen reader was told the playlist was stopped while it was running. It reports state and
 * toggles state now — the same behaviour the playlist page's header button already had.
 *
 * THE STATE IS PASSED IN, not read. `playing` and `active` come from the surface, which
 * derives them from playerStore through usePlayer and lib/playback.queueMatches. This card
 * subscribes to nothing: one authoritative source of playback state, and a component that
 * held its own would be the second one.
 */
import { ListMusic, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { ActionMenu, IconButton } from '../ui';
import PlaylistArtwork from './PlaylistArtwork';
import PlayingIndicator from '../player/PlayingIndicator';
import { c } from '../../styles/theme';
import type { Playlist, Song } from '../../types/domain';

export interface PlaylistCardProps {
  playlist: Playlist;
  /** The playlist's songs, resolved and in order. */
  songs: Song[];
  /** "3 tracks · 9:45". */
  summary: string;
  /**
   * True when the player is on THIS playlist's queue AND advancing.
   *
   * `queueMatches(queue, ids) && !paused` at the caller. What names the control "Pause" and
   * what draws the waveform in it.
   */
  playing?: boolean;
  /**
   * True when the player is on this playlist's queue, running or paused.
   *
   * What decides whether a press RESUMES or STARTS: pausing a playlist half way through
   * track 4 must not mean the next press restarts it from track 1. Defaults to `playing`,
   * for a caller with no pause state to report.
   */
  active?: boolean;
  onOpen: (playlist: Playlist) => void;
  /**
   * Starts this playlist — or pauses/resumes it when it is already the one playing.
   *
   * One handler for both, as PlaylistTrackRow's `onPlay` is: the surface knows which of the
   * two a press means, because the surface is where the player state comes from.
   */
  onPlay: (playlist: Playlist) => void;
  onRename: (playlist: Playlist) => void;
  onDelete: (playlist: Playlist) => void;
}

export default function PlaylistCard({
  playlist,
  songs,
  summary,
  playing = false,
  active = playing,
  onOpen,
  onPlay,
  onRename,
  onDelete,
}: PlaylistCardProps) {
  const empty = songs.length === 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(playlist)}
      onKeyDown={(e) => {
        // Space activates a role="button" natively and would otherwise scroll.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(playlist);
        }
      }}
      aria-label={`Open playlist ${playlist.name}`}
      className="lyx-track-row"
      style={{
        // Highlighted while this playlist is the one loaded, including while paused — the
        // same rule the song row and the playlist track row follow, so "the thing you are
        // on" looks the same on all three.
        border: `1px solid ${active ? c.accentBorder : c.border}`,
        borderRadius: 12,
        background: c.surface1,
        padding: 12,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <PlaylistArtwork songs={songs} size={48} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontWeight: 500,
              fontSize: 13,
              margin: '0 0 3px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {playlist.name}
          </p>
          <p
            style={{
              fontSize: 11,
              color: c.text2,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <ListMusic size={11} color={c.text3} aria-hidden="true" />
            {summary}
          </p>
        </div>

        <div
          className="lyx-row-controls"
          style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* The app's one playing visual, on the playlists list as well — see
              player/PlayingIndicator. Seeded on the playlist's id rather than a song's, so the
              shape belongs to the row it is on; it is the same generator either way
              (lib/waveform.indicatorHeights takes any seed).

              BESIDE THE CONTROL, NOT INSIDE IT (Batch 6 §13): the accent circle beside this is
              a normal play/pause button, which is what it was before the indicator existed.
              Rendered only while playing, like every other row surface — a row has nothing to
              hold the space for, unlike the global player bar. */}
          {playing && <PlayingIndicator seed={playlist.id} playing />}

          <IconButton
            /*
              The name follows the state, which it did not before: this control was
              permanently "Play <playlist>", so the playlist you were listening to offered to
              play itself and assistive technology was told the wrong thing about the one
              piece of state on the row.
            */
            label={playing ? `Pause ${playlist.name}` : `Play ${playlist.name}`}
            stopPropagation
            /*
              Disabled rather than hidden on an empty playlist: the control is where it
              will be once a track is added, and a button that vanishes is a layout
              that moves. The hook refuses an empty playlist anyway and says why, so
              this is belt and braces on the same rule.
            */
            disabled={empty}
            onClick={() => onPlay(playlist)}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: c.accentFill,
              flexShrink: 0,
            }}
          >
            {/* A normal play/pause control: the glyph is which way the press goes, and the
                waveform beside it is whether anything is playing. */}
            {playing ? (
              <Pause size={13} color="#1A1300" />
            ) : (
              <Play size={13} color="#1A1300" style={{ marginLeft: 1 }} />
            )}
          </IconButton>

          <ActionMenu
            label={`More actions for ${playlist.name}`}
            stopPropagation
            triggerStyle={{ width: 30, height: 30 }}
            items={[
              {
                label: 'Rename',
                // Named after the playlist: a screen reader reads a menu item out of
                // the row's context, so "Rename" alone would not say what.
                ariaLabel: `Rename ${playlist.name}`,
                icon: <Pencil size={13} color={c.text2} />,
                onSelect: () => onRename(playlist),
              },
              {
                label: 'Delete',
                ariaLabel: `Delete ${playlist.name}`,
                icon: <Trash2 size={13} color={c.danger} />,
                onSelect: () => onDelete(playlist),
                danger: true,
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
